/**
 * Shared ingredient parsing utility
 *
 * This module provides consistent ingredient parsing across the app,
 * used by both recipe detail display and grocery list generation.
 *
 * Handles:
 * - Leading quantity format: "1/4 cup all-purpose flour"
 * - Trailing quantity format: "Poulet entier, coupé en morceaux-1"
 * - Unicode fractions: ½, ¼, ¾, ⅓, ⅔, ⅛, ⅜, ⅝, ⅞
 * - French units: "c. à table", "c. à thé", "c à soupe" (no period), "tasse"
 * - Complex formats: "0.33 ml (¼ c. à thé) de zeste de lime"
 */

/**
 * Result of parsing an ingredient text
 */
export interface ParsedIngredient {
  /** The numeric part as a string (e.g., "1/4", "2", "0.33") */
  numPart: string;
  /** The unit part (e.g., "cup", "ml", "g") */
  unitPart: string;
  /** The remaining ingredient name */
  remainder: string;
}

/**
 * Unicode fraction to decimal mapping
 */
const FRACTION_MAP: Record<string, number> = {
  '½': 0.5,
  '¼': 0.25,
  '¾': 0.75,
  '⅓': 0.33,
  '⅔': 0.67,
  '⅛': 0.125,
  '⅜': 0.375,
  '⅝': 0.625,
  '⅞': 0.875,
};

/**
 * Parse a number string that may contain fractions or unicode fractions.
 * Examples:
 *   "1/2" → 0.5
 *   "1 1/2" → 1.5
 *   "½" → 0.5
 *   "1½" → 1.5
 *   "0.33" → 0.33
 *   "1 to ½" → 1 (max of range)
 *   "1 to 4" → 4 (max of range)
 */
export function parseNumStr(numStr: string): number {
  if (!numStr || !numStr.trim()) return 0;

  // "1-1/2" is one and a half, the way US recipes write it — not a range.
  const mixed = numStr.trim().match(/^(\d+)\s*-\s*(\d+)\/(\d+)$/);
  if (mixed && Number(mixed[3]) !== 0) {
    return Number(mixed[1]) + Number(mixed[2]) / Number(mixed[3]);
  }

  // A range — "2 to 3", « 2 à 3 », "2-3", "2–3" — buys the high end.
  const RANGE_SEPARATOR = /\s+to\s+|\s+à\s+|\s*[-–]\s*/i;
  if (RANGE_SEPARATOR.test(numStr.trim())) {
    const parts = numStr.trim().split(RANGE_SEPARATOR).filter(Boolean);
    if (parts.length > 1) {
      return Math.max(...parts.map(p => parseNumStr(p.trim())));
    }
  }

  // A comma is a French decimal (« 2,5 kg ») unless three digits follow it,
  // which is an English thousands separator ("1,500 g").
  numStr = numStr.replace(/(\d),(\d{3})(?!\d)/g, '$1$2').replace(/(\d),(\d)/g, '$1.$2');

  let num = 0;

  // Handle ASCII fractions like "1/2" or "1 1/2"
  if (numStr.includes('/')) {
    const parts = numStr.split(/\s+/);
    for (const part of parts) {
      if (part.includes('/')) {
        const [n, d] = part.split('/').map(Number);
        if (d !== 0) {
          num += n / d;
        }
      } else if (!isNaN(Number(part))) {
        num += Number(part);
      }
    }
  } else {
    // Handle unicode fractions
    let parsed = numStr;
    for (const [frac, val] of Object.entries(FRACTION_MAP)) {
      if (parsed.includes(frac)) {
        num += val;
        parsed = parsed.replace(frac, '');
      }
    }
    const remaining = parseFloat(parsed);
    if (!isNaN(remaining)) {
      num += remaining;
    }
  }

  return num;
}

/**
 * Common cooking fraction lookup table.
 * Maps fractional part → display string, with tolerance for floating-point imprecision.
 */
const COOKING_FRACTIONS: Array<{ value: number; display: string }> = [
  { value: 1 / 8, display: '1/8' },
  { value: 1 / 4, display: '1/4' },
  { value: 1 / 3, display: '1/3' },
  { value: 3 / 8, display: '3/8' },
  { value: 1 / 2, display: '1/2' },
  { value: 5 / 8, display: '5/8' },
  { value: 2 / 3, display: '2/3' },
  { value: 3 / 4, display: '3/4' },
  { value: 7 / 8, display: '7/8' },
];

const FRACTION_TOLERANCE = 0.02;

/**
 * Convert a decimal number to a fraction string for common cooking values.
 * Returns null if the number doesn't match a common cooking fraction.
 *
 * Examples:
 *   0.5   → "1/2"
 *   0.333 → "1/3"
 *   1.5   → "1 1/2"
 *   2.333 → "2 1/3"
 *   3.0   → "3"
 *   0.4   → null  (no common fraction match)
 */
export function toFraction(num: number): string | null {
  // Whole integer
  if (Number.isInteger(num) || Math.abs(num - Math.round(num)) < FRACTION_TOLERANCE) {
    return Math.round(num).toString();
  }

  const whole = Math.floor(num);
  const fractional = num - whole;

  // Check fractional part against cooking fractions
  for (const { value, display } of COOKING_FRACTIONS) {
    if (Math.abs(fractional - value) < FRACTION_TOLERANCE) {
      return whole > 0 ? `${whole} ${display}` : display;
    }
  }

  // No common fraction match
  return null;
}

/**
 * Units where decimals are more natural than fractions (metric/weight/volume).
 */
const DECIMAL_UNITS = new Set([
  'g', 'gram', 'grams', 'kg', 'kilogram', 'kilograms',
  'ml', 'milliliter', 'milliliters', 'l', 'liter', 'liters',
  'oz', 'ounce', 'ounces', 'lb', 'lbs', 'pound', 'pounds',
]);

/**
 * Format a number nicely for display, preferring fractions for common cooking values.
 * When a metric/weight unit is provided, skips fractions and uses decimals instead.
 *
 * Examples:
 *   formatQuantity(0.5)          → "1/2"
 *   formatQuantity(0.667)        → "2/3"
 *   formatQuantity(1.5)          → "1 1/2"
 *   formatQuantity(2.0)          → "2"
 *   formatQuantity(0.57, 'kg')   → "0.57"   (metric unit → decimal)
 *   formatQuantity(333, 'ml')    → "333"     (metric unit → decimal)
 *   formatQuantity(15.7)         → "16"      (large numbers rounded)
 */
export function formatQuantity(num: number, unit?: string): string {
  const useDecimal = unit && DECIMAL_UNITS.has(unit.toLowerCase());

  // Try fraction first (only for non-metric units)
  if (!useDecimal) {
    const fraction = toFraction(num);
    if (fraction !== null) return fraction;
  }

  // Decimal formatting
  if (num < 1) {
    return num.toFixed(2).replace(/\.?0+$/, '');
  } else if (num < 10) {
    return num.toFixed(1).replace(/\.0$/, '');
  }
  return Math.round(num).toString();
}

/**
 * Parse ingredient text to extract quantity, unit, and remaining ingredient name.
 *
 * This is the main parsing function used by both recipe detail and grocery list.
 * It handles both leading and trailing quantity formats.
 *
 * Examples:
 *   "1/4 cup all-purpose flour" → { numPart: "1/4", unitPart: "cup", remainder: "all-purpose flour" }
 *   "4 thin-cut boneless chicken breasts" → { numPart: "4", unitPart: "", remainder: "thin-cut boneless chicken breasts" }
 *   "Salt and black pepper" → { numPart: "", unitPart: "", remainder: "Salt and black pepper" }
 *   "Poulet entier, coupé en morceaux-1" → { numPart: "1", unitPart: "", remainder: "Poulet entier, coupé en morceaux" }
 *   "Riz gluant-900 g (2 lb)" → { numPart: "900", unitPart: "g", remainder: "Riz gluant" }
 *   "0.33 ml (¼ c. à thé) de zeste de lime" → { numPart: "0.33", unitPart: "ml", remainder: "zeste de lime" }
 */
/**
 * French counting words that measure a food the way "cloves" and "sprigs" do in
 * English. Without them "4 brins de thym" had no unit, so the grocery row read
 * « 4 thym », and "1 poignée basilic" kept « poignée » in the name (#772).
 * Each one refuses a following letter, or "brins" would take the start of
 * « brindilles » and "tiges" of « tigés ».
 */
const FRENCH_COUNT_UNITS = [
  'gousses?', 'brins?', 'bottes?', 'bouquets?', 'branches?', 'tiges?',
  'tranches?', 'poign[ée]es?', 'pinc[ée]es?',
].map(unit => `${unit}(?![a-zà-ÿ])`).join('|');

/**
 * Metric units written out, in both languages. A cook typing a line in the
 * recipe editor writes « 3 litres », not « 3 L », and only the English
 * spellings were known: « 3 Litres de sauces » kept its unit in the name, and
 * « 3 grammes » matched "gram" and left « mes de sel » (#792). Every one refuses
 * a following letter, so "cl" never takes the start of « clémentines ».
 */
const METRIC_WORD_UNITS = [
  'kilogrammes?', 'kilograms?', 'grammes?', 'grams?',
  'millilitres?', 'milliliters?', 'centilitres?', 'centiliters?', 'cl', 'litres?', 'liters?',
].map(unit => `${unit}(?![a-zà-ÿ])`).join('|');

// Leading quantity and unit, ANCHORED at the start so a number inside
// "(540 ml/19 oz)" is never read. See parseIngredientText.
const QUANTITY_PATTERN = new RegExp(String.raw`^([\d½¼¾⅓⅔⅛⅜⅝⅞]+(?:[.,]\d+)?(?:\s*[\d½¼¾⅓⅔⅛⅜⅝⅞\/]+)?(?:\s*(?:-|–|to|à)\s*[\d½¼¾⅓⅔⅛⅜⅝⅞]+(?:[.,]\d+)?(?:\s*[\d½¼¾⅓⅔⅛⅜⅝⅞\/]+)?)?)\s*(?:\([^)]+\)\s*)?(cuill[eè]res?\s+[àa]\s+(?:soupe|table|th[ée]|caf[ée])|c\.?\s*[àa]\s*(?:soupe|table|th[ée]|caf[ée])|c\.?\s*[àa]\s*[st]\.|cups?|tablespoons?|tbsp|teaspoons?|tsp|pounds?|lbs?|ounces?|oz|${METRIC_WORD_UNITS}|g(?![a-z])|kg|ml|l(?![a-z])|tasses?|cloves?|cans?|packages?|bunche?s?|heads?|pieces?|slices?|pinche?s?|dashe?s?|sprigs?|stalks?|boîtes?|handfuls?(?![a-z])|${FRENCH_COUNT_UNITS})?\s*`, 'i');

// "large bunch cilantro", « 1 grosse poignée de basilic »: a unit behind a size word.
const SIZE_ADJ_UNIT_PATTERN = new RegExp(String.raw`^(?:(?:large|small|medium|big|whole|fresh|good|gros|grosses?|petites?|bonnes?|belles?)\s+)?(cups?|tablespoons?|tbsp|teaspoons?|tsp|ounces?|oz|${METRIC_WORD_UNITS}|g(?![a-z])|kg|ml|tasses?|bunche?s?|heads?|pieces?|cloves?|cans?|packages?|sprigs?|stalks?|handfuls?(?![a-z])|${FRENCH_COUNT_UNITS})\s*`, 'i');

/**
 * A quantity written as a word: « un oignon », « deux tomates », "two cloves".
 * Only before a word, and never « un peu » / "a few": those are not one of
 * anything. Bare "a"/"an" is left alone — in the catalogue it was as often
 * "a saucy hot sauce" as "a bay leaf". Before a number or a bracket
 * ("One (8-ounce) package") the word is part of a size, not the count.
 */
const NUMBER_WORDS: Record<string, string> = {
  un: '1', une: '1', deux: '2', trois: '3', quatre: '4', cinq: '5', six: '6',
  sept: '7', huit: '8', neuf: '9', dix: '10', onze: '11', douze: '12',
  one: '1', two: '2', three: '3', four: '4', five: '5', seven: '7',
  eight: '8', nine: '9', ten: '10', eleven: '11', twelve: '12',
};
const NUMBER_WORD_PATTERN = new RegExp(
  String.raw`^(${Object.keys(NUMBER_WORDS).join('|')})(?![a-zà-ÿ])\s+(?!(?:peu|few|little|bit|couple)(?![a-zà-ÿ]))(?=[a-zà-ÿœ])`,
  'i',
);

/** « un oignon » → "1 oignon"; anything else unchanged. */
function numberWordAsDigits(text: string): string {
  const match = text.match(NUMBER_WORD_PATTERN);
  if (!match) return text;
  return `${NUMBER_WORDS[match[1].toLowerCase()]} ${text.slice(match[0].length)}`;
}

export function parseIngredientText(text: string): ParsedIngredient {
  if (!text) return { numPart: '', unitPart: '', remainder: '' };

  const trimmed = numberWordAsDigits(text.trim());

  // Pattern for trailing quantity format (common in French recipes)
  // Matches: "Poulet entier, coupé en morceaux-1", "Riz gluant-900 g (2 lb)"
  // Uses greedy .+ to capture full ingredient name including commas
  const trailingPattern = /^(.+)-(\d+(?:\s*(?:g|kg|ml|l|oz|lb|lbs))?(?:\s*\([^)]+\))?)$/i;
  const trailingMatch = trimmed.match(trailingPattern);

  if (trailingMatch) {
    const ingredientPart = trailingMatch[1].trim();
    const quantityPart = trailingMatch[2].trim();

    // Extract number and unit from quantity (e.g., "900 g (2 lb)" → num=900, unit=g)
    const qtyMatch = quantityPart.match(/^(\d+)\s*(g|kg|ml|l|oz|lb|lbs)?/i);
    if (qtyMatch) {
      return {
        numPart: qtyMatch[1],
        unitPart: qtyMatch[2] || '',
        remainder: ingredientPart,
      };
    }

    // Non-numeric quantity (e.g., "Au goût", "Quantité suffisante")
    return { numPart: '', unitPart: '', remainder: `${ingredientPart} (${quantityPart})` };
  }

  // Pattern for leading quantity format (standard US/UK recipes)
  // IMPORTANT: This regex is ANCHORED at the start (^) to avoid matching numbers
  // from parenthetical content like "(540 ml/19 oz)"
  //
  // Matches: "1/4 cup", "1 1/2 tablespoons", "4", "2-3", "½", "1½", "0.33 ml",
  //          "1 to ½ cup" (range with "to"), "1 to 4 tablespoons", etc.
  // Note: Single-letter units (g, l) use negative lookahead (?![a-z]) to avoid matching "garlic" as "g arlic"
  const quantityPattern = QUANTITY_PATTERN;

  const match = trimmed.match(quantityPattern);

  if (match && match[0].trim()) {
    const numPart = match[1] || '';
    let unitPart = match[2] || '';
    let remainder = trimmed.slice(match[0].length).trim();

    // Remove parenthetical content that contains units/quantities
    // e.g., "(¼ c. à thé)" from "0.33 ml (¼ c. à thé) de zeste de lime"
    remainder = remainder.replace(/^\([^)]*(?:c\.|tasse|cup|ml|oz|lb|g\b|\d\s*l\b)[^)]*\)\s*/i, '');

    // Remove French prepositions at start: "de ", "d'", "du ", "des ".
    // Both apostrophes — scraped French copy uses the typographic one far more
    // often than the ASCII one, so matching only `'` left "d’eau" as the name.
    remainder = remainder.replace(/^(de\s+|d['’]|du\s+|des\s+)/i, '').trim();

    // If no unit was found, check if remainder starts with an optional size adjective
    // followed by a recognized unit word: "large bunch cilantro" → unit="bunch"
    if (!unitPart && numPart && remainder) {
      const sizeAdjUnitPattern = SIZE_ADJ_UNIT_PATTERN;
      const adjMatch = remainder.match(sizeAdjUnitPattern);
      if (adjMatch && adjMatch[1]) {
        unitPart = adjMatch[1];
        remainder = remainder.slice(adjMatch[0].length).trim();
        remainder = remainder.replace(/^(of\s+|de\s+|du\s+|des\s+|d['’])/i, '').trim();
      }
    }

    // Only return if we have a meaningful remainder (not just whitespace)
    if (remainder) {
      return { numPart, unitPart, remainder };
    }
  }

  // No quantity found - return full text as remainder
  return { numPart: '', unitPart: '', remainder: trimmed };
}

/**
 * Parse amount from ingredient text (returns a number).
 * This is a convenience wrapper around parseIngredientText for cases
 * where only the numeric amount is needed.
 *
 * Examples:
 *   "2 cups flour" → 2
 *   "1/2 teaspoon salt" → 0.5
 *   "0.33 ml (¼ c. à thé) de zeste de lime" → 0.33
 */
export function parseAmount(text: string): number | undefined {
  const { numPart } = parseIngredientText(text);
  if (!numPart) return undefined;

  const parsed = parseNumStr(numPart);
  return parsed > 0 ? parsed : undefined;
}

/**
 * Parse unit from ingredient text.
 * This is a convenience wrapper around parseIngredientText for cases
 * where only the unit is needed.
 *
 * Examples:
 *   "2 cups flour" → "cups"
 *   "500g chicken" → "g"
 *   "3 tomatoes" → ""
 */
export function parseUnit(text: string): string | undefined {
  const { unitPart } = parseIngredientText(text);
  return unitPart || undefined;
}

/**
 * Scale a quantity string by a ratio (e.g., servings / baseServings).
 * Returns the scaled quantity as a formatted string.
 * Pass unit to control fraction vs decimal formatting (metric units stay decimal).
 */
export function scaleQuantityStr(numStr: string, ratio: number, unit?: string): string {
  if (ratio === 1 || !numStr) return numStr;
  const parsed = parseNumStr(numStr);
  if (parsed === 0) return numStr;
  return formatQuantity(parsed * ratio, unit);
}

/**
 * Abbreviate common units for display
 */
export function abbreviateUnit(unit: string): string {
  const abbrev: Record<string, string> = {
    tablespoon: 'tbsp',
    tablespoons: 'tbsp',
    teaspoon: 'tsp',
    teaspoons: 'tsp',
    pound: 'lb',
    pounds: 'lb',
    ounce: 'oz',
    ounces: 'oz',
    gram: 'g',
    grams: 'g',
    kilogram: 'kg',
    kilograms: 'kg',
    liter: 'L',
    liters: 'L',
    milliliter: 'ml',
    milliliters: 'ml',
    litre: 'L',
    litres: 'L',
    millilitre: 'ml',
    millilitres: 'ml',
    gramme: 'g',
    grammes: 'g',
    kilogramme: 'kg',
    kilogrammes: 'kg',
    cup: 'cup',
    cups: 'cups',
    clove: 'clove',
    cloves: 'cloves',
    can: 'can',
    cans: 'cans',
    bunch: 'bunch',
    bunches: 'bunches',
    head: 'head',
    heads: 'heads',
    piece: 'pc',
    pieces: 'pcs',
    slice: 'slice',
    slices: 'slices',
    pinch: 'pinch',
    pinches: 'pinches',
    dash: 'dash',
    dashes: 'dashes',
    sprig: 'sprig',
    sprigs: 'sprigs',
    stalk: 'stalk',
    stalks: 'stalks',
    boîte: 'boîte',
    boîtes: 'boîtes',
    'cuillère à soupe': 'c. à s.',
    'cuillères à soupe': 'c. à s.',
    'cuillère à thé': 'c. à t.',
    'cuillères à thé': 'c. à t.',
    'c. à soupe': 'c. à s.',
    'c. à thé': 'c. à t.',
  };
  // Spoons are written every way: "c à soupe" with no period (La Cuisine de
  // Jean-Philippe), "c. à table", "c.à soupe", "cuillère à café" (#794).
  const spoon = unit.toLowerCase().replace(/\s+/g, ' ').trim();
  if (/^(?:c\.?|cuill[eè]res?) ?[àa] ?(?:soupe|table|s\.)$/.test(spoon)) return 'c. à s.';
  if (/^(?:c\.?|cuill[eè]res?) ?[àa] ?(?:th[ée]|caf[ée]|t\.)$/.test(spoon)) return 'c. à t.';
  return abbrev[unit.toLowerCase()] || unit;
}

/**
 * Build the structured `extendedIngredients` shape from a recipe's plain
 * ingredient lines.
 *
 * Every read path in the app and on the server asks for `extendedIngredients`;
 * `ingredients` (a bare string array) is the legacy shape that predates it. A
 * document carrying only the legacy field renders no ingredients, contributes
 * nothing to a grocery list, and is skipped by the nightly translation sweep —
 * and because `backfillHasIngredients` in the cron runner accepts *either*
 * field, the self-healing backfill scores it complete and never repairs it.
 * 139 documents were in exactly that state (#710): 26 catalogue recipes, 57
 * saved copies, and all 56 browser-extension imports.
 *
 * This is a pure local conversion — no scrape, no model call. The text is
 * already on the document; only its shape is wrong.
 *
 * `original` keeps the whole line, because that is what every display and the
 * grocery aggregator parse quantities from. `name` gets the food alone, which
 * is what the pantry matcher and the step-ingredient chips read. Measured on
 * the 26 catalogue recipes, the resulting lines resolve to a canonical
 * ingredient 93.5% of the time against a shipped-catalog baseline of 87.0%
 * (`npm run eval:ingredients -- --corpus`).
 *
 * Blank and whitespace-only lines are dropped rather than carried as empty
 * rows: an empty `name` cannot group on a shopping list, and a row that shows
 * nothing is worse than no row.
 */
export function buildExtendedIngredients(
  lines: ReadonlyArray<string | null | undefined>,
): Array<{ id: number; name: string; original: string; amount: number; unit: string }> {
  if (!Array.isArray(lines)) return [];
  const out: Array<{ id: number; name: string; original: string; amount: number; unit: string }> = [];
  for (const line of lines) {
    if (typeof line !== 'string') continue;
    const original = line.trim();
    if (!original) continue;
    const { numPart, unitPart, remainder } = parseIngredientText(original);
    const amount = numPart ? parseNumStr(numPart) : 0;
    out.push({
      id: out.length,
      // Fall back to the whole line rather than an empty name: a line the
      // parser cannot split ("Salt and black pepper") still names a food.
      name: remainder.trim() || original,
      original,
      amount: Number.isFinite(amount) && amount > 0 ? amount : 0,
      unit: unitPart || '',
    });
  }
  return out;
}
